import { Content, Markdown } from '@/lib/markdown';
import { ctfImage } from '@/lib/contentful-url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { SwiperRef } from 'swiper/react';
import Button from './Button';

interface IHeader {
	className?: string;
	small?: boolean;
	title: string;
	headerTitleHighlightedText?: string;
	headerDescription?: Content | string;
	headerSliderImagesCollection: {
		items: {
			url: string;
			title: string;
		}[];
	};
}
export default function Header(props: IHeader) {
	const swiperRef = useRef<SwiperRef>(null);

	const splitText = props?.title?.split?.(
		new RegExp(`(${props?.headerTitleHighlightedText})`, 'g')
	);

	const [currentImage, setCurrentImage] = useState(0);
	const [fadeOut, setFadeOut] = useState(false);
	const $imageRef = useRef<HTMLImageElement>(null);

	const SLIDE_DURATION = 10000;
	useEffect(() => {
		if (!(props?.headerSliderImagesCollection?.items?.length > 1)) return;

		setTimeout(() => {
			$imageRef?.current?.classList.add('opacity-15');
		}, SLIDE_DURATION - 200);
		setTimeout(() => {
			$imageRef?.current?.classList.remove('opacity-15');
		}, SLIDE_DURATION);

		setTimeout(() => {
			if (currentImage === props.headerSliderImagesCollection.items.length - 1) {
				setCurrentImage(0);
			} else {
				setCurrentImage((prev) => prev + 1);
			}
		}, SLIDE_DURATION);
	}, [currentImage]);

	const firstImageUrl = props.headerSliderImagesCollection?.items?.[0]?.url;

	return (
		<>
			{/* Preload the LCP hero image (first slide only). Plain <img> gets no
			    preload from Next, so without this the fetch starts only after the
			    parser reaches it. Browsers honor <link rel="preload"> in body. */}
			{!!firstImageUrl && (
				<link
					rel="preload"
					as="image"
					imageSrcSet={[828, 1200, 1600, 2000]
						.map((w) => `${ctfImage(firstImageUrl, { width: w, quality: 50 })} ${w}w`)
						.join(', ')}
					imageSizes="100vw"
				/>
			)}
			<div
				className={`relative  justify-center ${
					props.small ? 'min-h-[40dvh] lg:min-h-[60dvh]' : 'min-h-[100dvh]'
				}  md:text-center flex flex-col gap-6  ${props.className && props.className}
	
				`}
			>
				<div className="bg-brand-600 absolute top-0 left-0 w-screen h-full z-10 opacity-50" />
				<div className="bg-gradient-to-b from-brand-600/40 via-brand-600/60 to-brand-600/80 absolute top-0 left-0 w-screen h-full z-10" />
				{/* biome-ignore lint/performance/noImgElement: static export disables next/image optimization, so responsive variants must come from Contentful URL params via srcSet */}
				<img
					fetchPriority="high"
					loading="eager"
					decoding="async"
					sizes="100vw"
					srcSet={[828, 1200, 1600, 2000]
						.map(
							(w) =>
								`${ctfImage(
									props.headerSliderImagesCollection?.items?.[currentImage]?.url,
									{ width: w, quality: 50 }
								)} ${w}w`
						)
						.join(', ')}
					ref={$imageRef}
					className="absolute top-0 left-0 w-screen h-full object-cover transition-all"
					src={ctfImage(
						props.headerSliderImagesCollection?.items?.[currentImage]?.url,
						{ width: 1600, quality: 50 }
					)}
					alt={props.headerSliderImagesCollection?.items?.[currentImage]?.title}
				/>
				<div className="container z-10 relative max-w-7xl mx-auto ">
					<div className={`${props.small ? 'w-full flex justify-center' : ''}`}>
						<h1
							className={`z-10 mt-14 text-white font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] ${
								props.small
									? 'text-center text-4xl sm:text-5xl md:text-6xl max-w-[25ch]'
									: 'text-start max-w-[20ch]'
							} `}
						>
							{splitText?.map((text, index) =>
								text === props.headerTitleHighlightedText ? (
									<span key={index} className="text-primary-500 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
										{text}
									</span>
								) : (
									<span key={index} className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">{text}</span>
								)
							)}
						</h1>
					</div>

					<div className="flex gap-4 flex-col max-w-2xl text-lg mx-auto ">
						{!!props.headerDescription &&
						typeof props.headerDescription === 'string' ? (
							<p className="text-xl lg:text-2xl font-medium mt-4 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
								{props.headerDescription}
							</p>
						) : (
							!!props.headerDescription && (
								<div className="text-white [&_*]:drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
									<Markdown content={props?.headerDescription as Content} />
								</div>
							)
						)}
					</div>

					<div
						className={`flex gap-4 md:mx-auto ${
							props.small ? 'justify-center' : ''
						} flex-wrap mt-6`}
					>
						{!props.small ? (
							<Button
								href="/warehousing-solutions"
								endIcon={<FontAwesomeIcon icon={['far', 'arrow-right-long']} />}
							>
								Warehousing solutions
							</Button>
						) : null}
					</div>
				</div>

				{!props.small ? (
					<FontAwesomeIcon
						onClick={() => {
							window.scrollTo({
								top: window.innerHeight,
								behavior: 'smooth',
							});
						}}
						className="size-8 hidden lg:block hover:bottom-6 duration-500 transition-all cursor-pointer p-4 rounded-full absolute bottom-8 left-1/2 -translate-x-1/2 border-2 border-white z-30"
						icon={['far', 'chevron-down']}
					/>
				) : null}
			</div>
		</>
	);
}
